import React, { useState, useContext } from "react";
import {
  PaymentElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import { Box, Button, CircularProgress } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import UserContext from "./UserContext";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const primaryButtonSx = {
  fontFamily: SANS,
  fontSize: "0.95rem",
  fontWeight: 600,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 3,
  py: 1.3,
  borderRadius: "12px",
  color: "#fafaf7",
  backgroundColor: INK,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)",
  transition:
    "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1a1d36",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" },
  "&.Mui-disabled": {
    backgroundColor: "rgba(15, 17, 33, 0.18)",
    color: "rgba(255, 255, 255, 0.85)",
    boxShadow: "none"
  }
};

const paymentElementOptions = {
  paymentMethodOrder: ["card", "applePay"],
  business: { name: "Interview Lab" }
};

const CheckoutForm = props => {
  const { setSubscriptionType } = useContext(UserContext);
  const stripe = useStripe();
  const elements = useElements();
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setIsSubmitting(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {}
      });
      if (error) {
        props.handleClose(false);
      } else if (paymentIntent) {
        // Optimistic local update only. The Stripe webhook is the
        // authoritative writer of subscriptionType in DynamoDB; on next
        // session reload the truth from the User table wins.
        setSubscriptionType("PREMIUM");
        props.handleClose(true);
      }
    } catch (err) {
      props.handleClose(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
    >
      <PaymentElement
        options={paymentElementOptions}
        onReady={() => setIsPaymentElementReady(true)}
      />
      {isPaymentElementReady && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            pt: 1,
            borderTop: `1px solid ${INK_HAIRLINE}`
          }}
        >
          <Button
            type="submit"
            disabled={!stripe || isSubmitting}
            disableRipple
            sx={{ ...primaryButtonSx, width: "100%", mt: 1 }}
          >
            {isSubmitting ? (
              <CircularProgress size={18} sx={{ color: "#fafaf7" }} />
            ) : (
              "Pay $9.99 and start Pro"
            )}
          </Button>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
              color: INK_SOFT
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 12 }} />
            <Box
              component="span"
              sx={{
                fontFamily: SANS,
                fontSize: "0.78rem",
                lineHeight: 1.4
              }}
            >
              Encrypted by Stripe. Your card details never touch our servers.
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CheckoutForm;
